import { debounce } from "lodash";
import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { Form, actions } from "react-redux-form";
import { useParams } from "react-router";
import Alert from "react-s-alert";
import { bindActionCreators } from "redux";
import {
    KeyContactBreadcrumbs,
    KeyContactCreateBreadcrumbs,
} from "src/helpers/breadcrumbHelper";
import { SettingsInstitutionsSubMenu } from "src/helpers/subMenuHelper";
import useNavigate from "src/hooks/useNavigate";
import * as KeyContactActions from "../../../actions/keyContacts";
import * as NavigationActions from "../../../actions/navigation";
import { NotificationAlert } from "../../../components/common/index";
import {
    CheckboxFormInput,
    NumberFormInput,
    TextFormInput,
} from "../../../components/form/index";
import { FontAwesomeIcons } from "../../../constants/fontAwesomeIcons";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import { renderRegistrationFormSaveBlock } from "../../../helpers/alert";
import * as AuthenticationHelper from "../../../helpers/authentication";
import {
    RegistrationComponent,
    renderFormControls,
} from "../../../helpers/formHelper";
import { RegistrationFormComponent } from "../../../interfaces/forms/IRegistrationComponent";
import { ITmdState } from "../../../reducers/index";
import { IRegistrationFormState } from "../../../reducers/reactReduxForms/index";
import { InstitutionLookupsLayout } from "../../index";

import "./KeyContactPage.scss";

interface KeyContactPageProps {
    form: Dtos.KeyContact & IRegistrationFormState;
    formState: Dtos.FormState;
    formProperties: Dtos.FormProperty[];
    keyContact: Dtos.KeyContact;
    lookups: Dtos.Lookup[];
    loadingKeyContact: boolean;
    loadKeyContactFailure: boolean;
    loadKeyContactErrorCode: string;
    savingKeyContact: boolean;
    saveKeyContactSuccess: boolean;
    saveKeyContactFailure: boolean;
    creatingKeyContact: boolean;
    createKeyContactSuccess: boolean;
    validatingKeyContact: boolean;
    validationFailures: Dtos.ResponseError[];
    permissions?: Dtos.PersonnelTmdPermissions[];
    clearKeyContact: KeyContactActions.IKeyContactClearActionCreator;
    loadKeyContact: KeyContactActions.IKeyContactLoadByIdActionCreator;
    saveKeyContact: KeyContactActions.IKeyContactSaveActionCreator;
    createKeyContact: KeyContactActions.IKeyContactCreateActionCreator;
    validateKeyContact: KeyContactActions.IKeyContactValidateActionCreator;
    loadForm: typeof actions.load;
    changeForm: typeof actions.change;
    resetForm: typeof actions.reset;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const reduxFormName: string = "reduxForms.keyContact";

const KeyContactPage: FC<KeyContactPageProps> = ({
    form,
    formState,
    formProperties,
    keyContact,
    lookups,
    loadingKeyContact,
    loadKeyContactFailure,
    loadKeyContactErrorCode,
    savingKeyContact,
    saveKeyContactSuccess,
    saveKeyContactFailure,
    creatingKeyContact,
    createKeyContactSuccess,
    validatingKeyContact,
    validationFailures,
    permissions,
    clearKeyContact,
    loadKeyContact,
    saveKeyContact,
    createKeyContact,
    validateKeyContact,
    loadForm,
    changeForm,
    resetForm,
    setBreadcrumbs,
    setDrawerItems
}) => {
    //
    // -- Params --
    const { keyContactId } = useParams<{ keyContactId: string }>();
    const keyContactIdNumber = parseInt(keyContactId, 10);

    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- States --
    const [saveAndReturn, setSaveAndReturn] = useState<boolean>(false);
    const [initalised, setInitalised] = useState<boolean>(false);
    const [hasBeenSubmitted, setHasBeenSubmitted] = useState<boolean>(false);

    const loadReduxForm = () => {
        let extraData: Dtos.KeyContact = {} as Dtos.KeyContact;

        loadForm(reduxFormName, Object.assign({ ...keyContact }, extraData));
    };


    // Load key contact
    useEffect(() => {
        if (keyContactIdNumber && !keyContact && !loadingKeyContact) {
            setDrawerItems(SettingsInstitutionsSubMenu(permissions));
            setBreadcrumbs(KeyContactBreadcrumbs(keyContactIdNumber));
            loadKeyContact(keyContactIdNumber);
        } else if (
            !keyContactIdNumber &&
            !creatingKeyContact &&
            !createKeyContactSuccess
        ) {
            createKeyContact();
            setBreadcrumbs(KeyContactCreateBreadcrumbs());
            setDrawerItems([]);
        } else if (keyContact && !initalised) {
            loadReduxForm();
            setInitalised(true);
        }
    }, [
        keyContactIdNumber,
        keyContact,
        setDrawerItems,
        SettingsInstitutionsSubMenu,
        permissions,
        setBreadcrumbs,
        KeyContactBreadcrumbs,
        loadKeyContact,
        createKeyContact,
        initalised,
        setInitalised,
        loadReduxForm,
        loadingKeyContact,
        creatingKeyContact,
        createKeyContactSuccess,
        KeyContactCreateBreadcrumbs,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadKeyContactFailure) {
            navigate(`/error/${loadKeyContactErrorCode}`);
        }
    }, [loadKeyContactFailure, loadKeyContactErrorCode, navigate]);

    // Update breadcrumbs
    useEffect(() => {
        if (keyContactIdNumber && keyContact?.value) {
            setBreadcrumbs(
                KeyContactBreadcrumbs(keyContactIdNumber, keyContact?.value)
            );
        }
    }, [keyContactIdNumber, keyContact, setBreadcrumbs, KeyContactBreadcrumbs]);

    // Reload key contact after saving
    useEffect(() => {
        if (!savingKeyContact && saveKeyContactSuccess) {
            Alert.success(
                <NotificationAlert
                    alertContent={<span>Key contact saved successfully</span>}
                    icon={FontAwesomeIcons.Solid.CHECK}
                />
            );

            if (saveAndReturn) {
                let url: string = "/settings/institution/keycontacts";
                navigate(url);
            } else if (!keyContactIdNumber) {
                let url: string = `/settings/institution/keycontacts/${keyContact?.id}`;
                navigate(url);
                clearKeyContact();
            }
        }
    }, [
        savingKeyContact,
        saveKeyContactSuccess
    ]);

    // Failed saving
    useEffect(() => {
        if (!savingKeyContact && saveKeyContactFailure) {
            Alert.error(
                <NotificationAlert
                    alertContent={renderRegistrationFormSaveBlock()}
                    icon={FontAwesomeIcons.Solid.BAN}
                />
            );
        }
    }, [savingKeyContact, saveKeyContactFailure]);

    // Cleanup effect to clear the key contact on component unmount
    useEffect(() => {
        return () => {
            clearKeyContact();
            resetForm(reduxFormName);
        };
    }, [clearKeyContact, resetForm, reduxFormName]);

    const onCancel = () => {
        let url: string = "/settings/institution/keycontacts";
        navigate(url);
    };

    const handleOnSubmitFailed = (_data: Dtos.KeyContact) => {
        Alert.error(
            <NotificationAlert
                alertContent={renderRegistrationFormSaveBlock()}
                icon={FontAwesomeIcons.Solid.BAN}
            />
        );
    };

    const handleOnSubmit = async (data: any) => {
        if (!hasBeenSubmitted) {
            setHasBeenSubmitted(true);
        }

        if (data) {
            let changes: Dtos.KeyContact = {} as Dtos.KeyContact;

            await saveKeyContact(Object.assign({ ...data }, changes));

            if (data.saveAndReturn) {
                setSaveAndReturn(true);
            }
        }
    };

    // Debounce validation after the initial submit to clear form validation errors as the form get updated.
    useEffect(() => {
        const debounceValidate = debounce(async () => {
            let changes: Dtos.KeyContact = {} as Dtos.KeyContact;

            if (form && hasBeenSubmitted && !validatingKeyContact) {
                await validateKeyContact(Object.assign({ ...form }, changes));
            }
        }, 250);

        debounceValidate();

        return () => {
            debounceValidate.cancel();
        };
    }, [form]);

    const renderContent = useMemo(() => {
        const hasPermission: boolean =
            AuthenticationHelper.hasCreateEditPermission(
                form,
                permissions,
                Dtos.Permission.KeyContactsUpdate,
                Dtos.Permission.KeyContactsCreate
            );
        const formDisabled: boolean =
            !hasPermission ||
            savingKeyContact ||
            (formState && formState.formStatus == Dtos.FormStatus.Disabled);

        let formComponents: { [index: string]: RegistrationFormComponent } = {
            detailsGroup: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <h1>Details</h1>
                        <div className="card card-shadow" style={{ zIndex: 1 }}>
                            <div className="form-row pt-4 pb-2 px-4">
                                <div className="col-md-12">
                                    {renderFormControls(
                                        form,
                                        "keyContact",
                                        {
                                            value: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-2",
                                                fieldClassName:
                                                    "col-sm-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row pt-2 pb-4 px-4">
                                <div className="col-md-6">
                                    {renderFormControls(
                                        form,
                                        "keyContact",
                                        {
                                            rank: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: NumberFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-4",
                                                fieldClassName:
                                                    "col-sm-8 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                                <div className="col-md-6 d-flex align-items-center">
                                    {renderFormControls(
                                        form,
                                        "keyContact",
                                        {
                                            active: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: CheckboxFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    label: "Active",
                                                },
                                                inputLabel: " ",
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-sm-4",
                                                fieldClassName:
                                                    "col-sm-8 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                ),
            },
        };

        return (
            <div>
                <Form
                    model={reduxFormName}
                    onSubmit={(val, _event) => handleOnSubmit(val)}
                    onSubmitFailed={(val) => handleOnSubmitFailed(val)}
                >
                    {renderFormControls(
                        form,
                        "keyContact",
                        formComponents,
                        lookups,
                        validationFailures,
                        formProperties,
                        changeForm
                    )}
                    <div className="mt-4 mb-4 text-right">
                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                changeForm(
                                    reduxFormName + ".saveAndReturn",
                                    false
                                );
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit
                        </button>

                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                changeForm(
                                    reduxFormName + ".saveAndReturn",
                                    true
                                );
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit And Return
                        </button>
                        <button
                            onClick={onCancel}
                            className="btn btn-primary mr-2"
                        >
                            Cancel
                        </button>
                    </div>
                </Form>
            </div>
        );
    }, [
        form,
        permissions,
        savingKeyContact,
        formState,
        handleOnSubmit,
        handleOnSubmitFailed,
        lookups,
        validationFailures,
        formProperties,
        changeForm,
        onCancel,
    ]);

    return (
        <InstitutionLookupsLayout
            loading={loadingKeyContact}
            permission={Dtos.Permission.KeyContactsView}
        >
            {renderContent}
        </InstitutionLookupsLayout>
    );
};

const mapStateToProps = (state: ITmdState): Partial<KeyContactPageProps> => {
    let keyContact: Dtos.KeyContact | undefined = !(
        state.keyContacts.formData instanceof Array
    )
        ? state.keyContacts.formData
        : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        keyContact: keyContact!,
        loadingKeyContact:
            state.keyContacts.loadState &&
            state.keyContacts.loadState.status == RequestState.Pending,
        loadKeyContactFailure:
            state.keyContacts.loadState &&
            state.keyContacts.loadState.status == RequestState.Failure,
        loadKeyContactErrorCode: state.keyContacts.loadState.errorCode,
        savingKeyContact:
            state.keyContacts.saveState &&
            state.keyContacts.saveState.status == RequestState.Pending,
        saveKeyContactSuccess:
            state.keyContacts.saveState &&
            state.keyContacts.saveState.status == RequestState.Success,
        saveKeyContactFailure:
            state.keyContacts.saveState &&
            state.keyContacts.saveState.status == RequestState.Failure,
        creatingKeyContact:
            state.keyContacts.createState &&
            state.keyContacts.createState.status == RequestState.Pending,
        createKeyContactSuccess:
            state.keyContacts.createState &&
            state.keyContacts.createState.status == RequestState.Success,
        validatingKeyContact:
            state.keyContacts.validateState &&
            state.keyContacts.validateState.status == RequestState.Pending,
        permissions: permissions,
        lookups: [],
        form: state.reduxForms.keyContact,
        formState: state.keyContacts.formState,
        formProperties: state.keyContacts.formProperties,
        validationFailures: keyContact
            ? state.keyContacts.validationFailures
            : undefined,
    };
};

const mapDispatchToProps = (dispatch): Partial<KeyContactPageProps> => {
    return {
        loadForm: bindActionCreators(actions.load, dispatch),
        changeForm: bindActionCreators(actions.change, dispatch),
        resetForm: bindActionCreators(actions.reset, dispatch),
        loadKeyContact: bindActionCreators(
            KeyContactActions.LoadKeyContactById,
            dispatch
        ),
        clearKeyContact: bindActionCreators(KeyContactActions.Clear, dispatch),
        saveKeyContact: bindActionCreators(
            KeyContactActions.SaveKeyContact,
            dispatch
        ),
        createKeyContact: bindActionCreators(
            KeyContactActions.CreateKeyContact,
            dispatch
        ),
        validateKeyContact: bindActionCreators(
            KeyContactActions.ValidateKeyContact,
            dispatch
        ),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        )
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(KeyContactPage);
